import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, office, till } from "@/lib/api";
import { Link } from "react-router";
import { ago, personName } from "@/lib/format";
import { OFFICE_ROLE_LABEL, useSession, type OfficeRole } from "@/lib/session";
import { useTill } from "@/lib/till-context";
import { Avatar, Badge, Button, Field, Input, Select, cx, useConfirm, useToast, Pager, usePaged, PasswordInput, Modal } from "@/components/ui";
import { Card, PageHead, td, th } from "@/components/Layout";

type Member = { id: string; name: string; email: string | null; phone: string | null; role: OfficeRole; platform: boolean; lastSeenAt: number | null };
type Invite = { id: string; code: string; name: string; login: string; role: OfficeRole; invitedBy: string; expired: boolean; createdAt: number; expiresAt: number };

const ROLES: { id: OfficeRole; sub: string }[] = [
  { id: "gm", sub: "Everything, adds staff below them" },
  { id: "manager", sub: "Runs the shop day to day" },
  { id: "accountant", sub: "Reports and money owed" },
  { id: "stock", sub: "Receives and counts stock" },
  { id: "owner", sub: "Everything, adds people" },
];
/** An owner manages everyone; a general manager only managers, accountants and stock keepers. */
const canManage = (me: OfficeRole, role: string) => me === "owner" || (me === "gm" && role !== "owner" && role !== "gm");
const joinLink = (code: string) => `${window.location.origin}/join/${code}`;
const wa = (login: string, text: string) => (/^0\d{9}$/.test(login) ? `https://wa.me/${login.replace(/^0/, "233")}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`);

/** Who signs in to the back office, and with what role. The owner invites people by link. */
export function Team() {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const s = useSession();
  const { store, me } = useTill();
  const myRole: OfficeRole = me?.user.role ?? s.user?.role ?? "owner";
  // The admin's own roles from Roles & permissions sit alongside the built-in ones.
  const rolesQ = useQuery({ queryKey: ["permissions"], queryFn: () => api<{ roles: { id: string; label: string; custom: boolean }[] }>(till("/admin/permissions")) });
  const customRoles = (rolesQ.data?.roles ?? []).filter((r) => r.custom);
  const ALL_ROLES: { id: string; sub: string }[] = [...ROLES, ...customRoles.map((r) => ({ id: r.id, sub: "Set on Roles & permissions" }))];
  const labelOf = (id: string) => OFFICE_ROLE_LABEL[id as OfficeRole] ?? customRoles.find((r) => r.id === id)?.label ?? "Team member";
  const q = useQuery({ queryKey: ["team"], queryFn: () => api<{ members: Member[]; invites: Invite[] }>(office("/team")) });
  const [name, setName] = useState("");
  const [login, setLogin] = useState("");
  const [role, setRole] = useState<OfficeRole>("manager");
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState<Invite | null>(null);
  const direct = !!import.meta.env.VITE_BRAND;
  const [temp, setTemp] = useState<{ name: string; login: string; password: string; isNew: boolean; mustChange: boolean } | null>(null);
  const [pw, setPw] = useState("");
  const [mustChange, setMustChange] = useState(true);
  const [resetFor, setResetFor] = useState<Member | null>(null);
  const [adding, setAdding] = useState(false);
  const closePanel = () => { setAdding(false); setTemp(null); setResetFor(null); setPw(""); setMustChange(true); };
  const makePw = () => { const a = "abcdefghjkmnpqrstuvwxyzACDEFGHJKLMNPQRSTUVWXYZ2345679"; setPw(Array.from(crypto.getRandomValues(new Uint8Array(10)), (n) => a[n % a.length]).join("")); };
  const pwOk = pw.length >= 8;
  const addDirect = async () => {
    setBusy(true);
    try {
      const r = await api<{ person: { name: string; login: string }; tempPassword: string }>(office("/team-add"), { body: { name, login, role, password: pw, mustChange } });
      setTemp({ name: r.person.name, login: r.person.login, password: r.tempPassword, isNew: true, mustChange });
      setName("");
      setLogin("");
      setPw("");
      setMustChange(true);
      refresh();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't add them", "err");
    } finally {
      setBusy(false);
    }
  };
  const resetPassword = (m: Member) => { setTemp(null); setMade(null); setPw(""); setMustChange(true); setResetFor(m); };
  const saveReset = async () => {
    if (!resetFor) return;
    setBusy(true);
    try {
      const r = await api<{ tempPassword: string }>(office(`/team-temp/${resetFor.id}`), { body: { password: pw, mustChange } });
      setTemp({ name: personName(resetFor.name), login: resetFor.phone ?? resetFor.email ?? "", password: r.tempPassword, isNew: false, mustChange });
      setResetFor(null);
      setPw("");
      setMustChange(true);
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't change it", "err");
    } finally {
      setBusy(false);
    }
  };
  const pwBox = (
    <>
      <Field label="Password" hint="At least 8 characters. Type one, or tap Make one">
        <div className="flex gap-2">
          <span className="min-w-0 flex-1"><PasswordInput startShown value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Their password" autoComplete="new-password" className="font-mono" /></span>
          <Button type="button" onClick={makePw}>Make one</Button>
        </div>
      </Field>
      <label className="flex items-start gap-2.5 text-sm">
        <input type="checkbox" checked={mustChange} onChange={(e) => setMustChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[var(--color-brand)]" />
        <span><b className="font-semibold">Ask them to choose their own password</b><span className="block text-xs text-slate-500">When they first sign in. Untick to let them keep this one.</span></span>
      </label>
    </>
  );
  const shop = store?.storeName || "the shop";
  const message = (i: Pick<Invite, "name" | "role" | "code">) => `${s.user?.name ?? "The owner"} added you to the ${shop} back office as ${(OFFICE_ROLE_LABEL[i.role] ?? "a team member").toLowerCase()}. Set your password here (works once, for 48 hours): ${joinLink(i.code)}`;
  const refresh = () => void qc.invalidateQueries({ queryKey: ["team"] });

  const invite = async () => {
    setBusy(true);
    try {
      const r = await api<{ invite: Invite }>(office("/team/invites"), { body: { name, login, role } });
      setMade(r.invite);
      setName("");
      setLogin("");
      refresh();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't make the invite", "err");
    } finally {
      setBusy(false);
    }
  };
  const act = async (path: string, body: unknown, done: string) => {
    try {
      const r = await api<{ invite?: Invite }>(office(path), { body });
      toast(done);
      refresh();
      return r;
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save", "err");
    }
  };
  const copy = (text: string) => void navigator.clipboard?.writeText(text).then(() => toast("Link copied"));

  const members = (q.data?.members ?? []).filter((m) => !m.platform);
  const invites = q.data?.invites ?? [];
  const [find, setFind] = useState("");
  const shown = members.filter((m) => !find.trim() || [m.name, m.email, m.phone].some((v) => v?.toLowerCase().includes(find.trim().toLowerCase())));
  const memberPage = usePaged(shown);
  return (
    <>
      <PageHead title="Team (back office)" sub="People who sign in to this back office with a password. Till staff use a PIN, on Staff & roles.">{direct ? <Button tone="primary" onClick={() => { closePanel(); setAdding(true); }}>Add person</Button> : null}</PageHead>
      <div className={cx("grid gap-4", !direct && "xl:grid-cols-[minmax(0,1fr)_380px]")}>
        <div className="flex min-w-0 flex-col gap-4">
          <Card title={`People (${members.length})`} right={<Input aria-label="Search people" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Search name, phone or email" className="!h-10 !w-60 max-w-full" />}>
            {q.isLoading ? <p className="px-5 pb-5 text-sm text-slate-500">Loading…</p> : null}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead><tr><th className={th}>Person</th><th className={th}>Phone or email</th><th className={th}>Last signed in</th><th className={th}>Role</th><th className={th}><span className="sr-only">Actions</span></th></tr></thead>
                <tbody>
                  {memberPage.rows.map((m) => {
                    const mine = m.id === s.user?.id;
                    const can = !mine && canManage(myRole, m.role);
                    return (
                      <tr key={m.id}>
                        <td className={td}>
                          <span className="flex min-w-0 items-center gap-2.5">
                            <Avatar name={personName(m.name)} color="#f2c3a0" size={34} />
                            <span className="min-w-0"><b className="block truncate font-semibold">{personName(m.name)}{mine ? <span className="ml-1 font-normal text-slate-500">(you)</span> : null}</b></span>
                          </span>
                        </td>
                        <td className={cx(td, "text-[13px] text-slate-600")}><span className="block max-w-[260px] truncate">{m.email ?? m.phone ?? "-"}</span></td>
                        <td className={cx(td, "whitespace-nowrap text-[13px]")}>{m.lastSeenAt ? <span className="text-slate-600">{ago(m.lastSeenAt)}</span> : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">Not signed in yet</span>}</td>
                        <td className={td}>
                          {can ? (
                            <Select aria-label={`Role for ${personName(m.name)}`} value={m.role} onChange={(e) => void act(`/team/${m.id}`, { role: e.target.value }, "Role changed")} className="!h-10 !w-48">
                              {ALL_ROLES.filter((r) => r.id === m.role || canManage(myRole, r.id)).map((r) => <option key={r.id} value={r.id}>{labelOf(r.id)}</option>)}
                            </Select>
                          ) : <Badge tone={m.role === "owner" ? "dark" : "slate"}>{labelOf(m.role)}</Badge>}
                        </td>
                        <td className={cx(td, "w-[1%] whitespace-nowrap")}>
                          <span className="flex flex-col items-stretch gap-1.5 2xl:flex-row">
                          {can && direct ? <Button size="sm" onClick={() => void resetPassword(m)}>New password</Button> : null}{can ? <Button size="sm" tone="danger" onClick={async () => { if (await confirm({ title: `Remove ${personName(m.name)}?`, body: "They can't sign in to the back office any more. Their past work stays in the reports.", ok: "Remove", danger: true })) void act(`/team/${m.id}`, { remove: true }, `${personName(m.name)} removed`); }}>Remove</Button> : null}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {q.data && !shown.length ? <p className="px-5 py-8 text-center text-sm text-slate-500">{find ? "Nobody matches." : "Nobody yet. Add your first person."}</p> : null}
            <Pager page={memberPage.page} size={memberPage.size} total={memberPage.total} onPage={memberPage.setPage} />
          </Card>
          {invites.length ? (
            <Card title={`Invites waiting (${invites.length})`}>
              {invites.map((i) => (
                <div key={i.id} className="flex flex-wrap items-center gap-3 border-t border-slate-100 px-5 py-3 text-sm">
                  <span className="grid h-[34px] w-[34px] flex-none place-items-center rounded-full border-2 border-dashed border-slate-300 text-xs font-bold text-slate-500">{i.name.slice(0, 2).toUpperCase()}</span>
                  <span className="min-w-[200px] flex-1"><b className="block font-semibold">{i.name}</b><span className="text-xs text-slate-500">{i.login} · {labelOf(i.role)}{i.expired ? "" : ` · invited ${ago(i.createdAt)}`}</span></span>
                  {i.expired ? <Badge tone="amber">Link ran out</Badge> : <Badge>Not joined yet</Badge>}
                  <Button size="sm" onClick={async () => { const r = await act(`/team/invites/${i.id}/resend`, {}, "New link made"); if (r?.invite) setMade(r.invite); }}>{i.expired ? "New link" : "Resend"}</Button>
                  <Button size="sm" onClick={() => void act(`/team/invites/${i.id}/cancel`, {}, "Invite cancelled")}>Cancel</Button>
                </div>
              ))}
            </Card>
          ) : null}
          <p className="text-[13px] text-slate-500">What each role can do is set on <Link to="/permissions" className="font-semibold text-brand underline">Roles & permissions</Link>.</p>
        </div>

        {(() => {
          const panelTitle = temp ? (temp.isNew ? `${temp.name} is added` : `New password for ${temp.name}`) : resetFor ? `New password for ${personName(resetFor.name)}` : made ? `Invite for ${made.name}` : "Add a person";
          const panel = (<>
          {temp ? (
            <div className="flex flex-col gap-3 px-5 pb-5 text-sm">
              <div className="rounded-xl bg-brand-tint px-3.5 py-3 text-brand-ink"><span className="block text-xs font-semibold">Signs in with</span><b className="block break-all text-[15px]">{temp.login}</b><span className="mt-2 block text-xs font-semibold">{temp.mustChange ? "Temporary password" : "Password"}</span><b className="block break-all font-mono text-xl tracking-wide">{temp.password}</b></div>
              <p className="text-xs font-semibold text-warn-ink">This is the only time it shows. Copy it now and give it to them privately.</p>
              <Button onClick={() => void navigator.clipboard?.writeText(temp.password).then(() => toast("Password copied"))}>Copy the password</Button>
              <span className="text-xs text-slate-500">They sign in at {window.location.host}{temp.mustChange ? " and must choose their own password straight away." : " with this password."}</span>
              <Button tone="primary" onClick={closePanel}>Done</Button>
            </div>
          ) : resetFor ? (
            <div className="flex flex-col gap-3 px-5 pb-5">
              <p className="text-sm text-slate-600">Their old password stops working straight away.</p>
              {pwBox}
              <Button tone="primary" disabled={busy || !pwOk} onClick={() => void saveReset()}>{busy ? "Saving…" : "Save the new password"}</Button>
              <Button onClick={closePanel}>Cancel</Button>
            </div>
          ) : made ? (
            <div className="flex flex-col gap-3 px-5 pb-5 text-sm">
              <p className="rounded-lg bg-[#e7f5e4] px-3 py-3 leading-relaxed">{message(made)}</p>
              <a href={wa(made.login, message(made))} target="_blank" rel="noreferrer" className="grid h-12 place-items-center rounded-xl bg-brand font-bold text-white">
                Send on WhatsApp
              </a>
              <Button onClick={() => copy(joinLink(made.code))}>Copy the link</Button>
              <span className="text-xs text-slate-500">The link works once and runs out in 48 hours. They choose their own password; nobody else sees it.</span>
              <Button onClick={() => setMade(null)}>Add someone else</Button>
            </div>
          ) : (
            <div className="flex flex-col gap-3 px-5 pb-5">
              <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Yaw Boakye" maxLength={80} />
              </Field>
              <Field label="Phone or email" hint="What they'll sign in with">
                <Input value={login} onChange={(e) => setLogin(e.target.value)} placeholder="020 555 7781 or name@shop.com" />
              </Field>
              <span className="text-[13px] font-semibold">Role</span>
              <div className="grid grid-cols-2 gap-2">
                {ALL_ROLES.filter((r) => canManage(myRole, r.id)).map((r) => (
                  <button key={r.id} type="button" aria-pressed={role === r.id} onClick={() => setRole(r.id as OfficeRole)} className={cx("flex flex-col items-start gap-0.5 rounded-xl border-[1.5px] px-3 py-2.5 text-left", role === r.id ? "border-brand bg-brand-tint" : "border-slate-200 bg-white")}>
                    <b className="text-sm">{labelOf(r.id)}</b>
                    <span className="text-xs text-slate-500">{r.sub}</span>
                  </button>
                ))}
              </div>
              {direct ? pwBox : null}
              <Button tone="primary" disabled={busy || !name.trim() || !login.trim() || (direct && !pwOk)} onClick={() => void (direct ? addDirect() : invite())} className="mt-1">
                {direct ? (busy ? "Adding…" : "Add person") : busy ? "Making the invite…" : "Make an invite link"}
              </Button>
            </div>
          )}
          </>);
          if (!direct) return <Card title={panelTitle}>{panel}</Card>;
          if (!adding && !temp && !resetFor) return null;
          return <Modal title={panelTitle} onClose={closePanel}><div className="-mx-5 -my-4 pt-1">{panel}</div></Modal>;
        })()}
      </div>
    </>
  );
}
